import React from 'react';
import { View, Pressable, TextInput, type StyleProp, type ViewStyle } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useHospitalityStore } from '../store/useHospitalityStore';
import { useAppStore } from '../store/useAppStore';
import { useTheme } from '../theme';
import type { Theme } from '../theme';
import { Screen, Row, Col, Spread } from '../components/Layout';
import Txt from '../components/Txt';
import Icon from '../components/Icon';
import Button from '../components/Button';
import Chip from '../components/Chip';
import Divider from '../components/Divider';
import { Input } from '../components/Input';
import { requestGuestOtp, verifyGuestOtp } from '../services/guest/guest';
import { displayTime } from '../data/format';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

/**
 * Guest self-join + WhatsApp confirmation — ported from SK Hospitality.dc.html
 * template lines 638-743 and guestVals() (lines 1620-1637). Two surfaces behind a
 * segmented toggle, both inside a 330px phone frame. React Native port.
 */

const WA_ACTIONS = ['Add to calendar', 'Get directions', 'Modify booking', 'View menu'];

/** The prototype's inline checkmark svg (no matching key in the ICONS set). */
function Check({ size, color, sw = 2 }: { size: number; color: string; sw?: number }) {
  return (
    <Svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <Path d="M5 12l5 5L20 7" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Segmented-toggle pill button (replaces the web `seg()` CSS helper). */
function Seg({ active, label, onPress, t }: { active: boolean; label: string; onPress: () => void; t: Theme }) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        paddingVertical: 8,
        paddingHorizontal: 15,
        borderRadius: 8,
        ...(active ? { backgroundColor: t.colors.card, ...t.shadows.sm } : { backgroundColor: 'transparent' }),
      }}
    >
      <Txt size="md" weight="medium" color={active ? 'ink' : 'ink3'}>{label}</Txt>
    </Pressable>
  );
}

export default function Guest() {
  const s = useHospitalityStore();
  const toast = useAppStore((st) => st.toast);
  const orgId = useAppStore((st) => st.orgId);
  const reservations = useHospitalityStore((st) => st.reservations);
  const t = useTheme();
  const { guestView, guestStep, guestPhone, guestOtp, guestPartySize, queuePos, queueState } = s;
  const venueName = s.location.name || 'Your venue';
  const venueInitials = (s.location.name || 'SK').replace(/[^A-Za-z ]/g, '').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || 'SK';

  const gQueue = guestStep === 'queue' && queueState !== 'ready';
  const gReady = guestStep === 'queue' && queueState === 'ready';

  // A real QR code encodes both org + location; this screen is a preview
  // rendered inside the operator's own logged-in session, so the org is
  // whichever tenant the operator is signed into (platform_org_id, same
  // claim every other real cross-service call in this app already uses).
  const tenantId = orgId;
  const locationId = s.apiLocationId ?? '';

  // Request OTP — keep the existing local step transition, layer the service on top.
  const handleGetCode = async () => {
    s.guestGetCode();
    try {
      const res = await requestGuestOtp({ tenantId, locationId, phone: guestPhone });
      showSuccess(res, 'OTP sent');
    } catch (err) {
      handleApiError(err, 'OTP request failed');
    }
  };

  // Verify OTP / join the waitlist. Advance the local UI optimistically so the
  // prototype flow works even if the backend is down; the service + toast layer on top.
  const handleJoinQueue = async () => {
    useHospitalityStore.setState({ guestStep: 'queue', queueState: 'waiting', queuePos: 1 });
    try {
      const res = await verifyGuestOtp({
        tenantId,
        locationId,
        phone: guestPhone,
        code: guestOtp.join(''),
        partySize: guestPartySize,
      });
      showSuccess(res, "You're in the queue");
      if (typeof res.queue?.position === 'number') {
        useHospitalityStore.setState({ queuePos: res.queue.position });
      }
    } catch (err) {
      handleApiError(err, 'Join queue failed');
    }
  };

  // Most recent confirmed booking — real data for the WhatsApp confirmation
  // preview (was 5 literal '—' placeholders regardless of what's actually booked).
  const confirmedRes = reservations.find((r) => r.status === 'confirmed') ?? null;

  // OTP digit boxes: real TextInputs with auto-advance, not display-only Views.
  const otpRefs = React.useRef<(TextInput | null)[]>([]);
  const handleOtpChange = (i: number, v: string) => {
    const digit = v.replace(/[^0-9]/g, '').slice(-1);
    const next = [...guestOtp];
    next[i] = digit;
    s.setGuestOtp(next);
    if (digit && i < guestOtp.length - 1) otpRefs.current[i + 1]?.focus();
  };
  const handleOtpKeyPress = (i: number, key: string) => {
    if (key === 'Backspace' && !guestOtp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  return (
    <Screen padding={0} style={{ alignItems: 'center', gap: 18, paddingTop: 26, paddingHorizontal: 24, paddingBottom: 50 }}>
      {/* segmented toggle */}
      <View style={{ flexDirection: 'row', backgroundColor: t.colors.paper3, borderRadius: 11, padding: 3, alignSelf: 'center' }}>
        <Seg active={guestView === 'join'} label="Self-join queue" onPress={() => s.setGuestView('join')} t={t} />
        <Seg active={guestView === 'whatsapp'} label="WhatsApp confirmation" onPress={() => s.setGuestView('whatsapp')} t={t} />
      </View>

      {/* phone frame */}
      <View style={{ width: 330, backgroundColor: '#0b0c0e', borderRadius: 44, padding: 11, ...t.shadows.lg }}>
        <View style={{ backgroundColor: t.colors.card, borderRadius: 33, height: 652, overflow: 'hidden' }}>
          {/* status bar */}
          <Spread style={{ height: 30, paddingHorizontal: 24 }}>
            <Txt mono size="sm" color="ink3">9:41</Txt>
            <Txt mono size="sm" color="ink3">5G ▮▮</Txt>
          </Spread>

          {/* ── SELF-JOIN ── */}
          {guestView === 'join' && (
            <View style={{ flex: 1 }}>
              <Row gap={9} style={{ justifyContent: 'center', paddingTop: 4, paddingHorizontal: 24, paddingBottom: 10 }}>
                <View style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: t.colors.accent, alignItems: 'center', justifyContent: 'center' }}>
                  <Txt color="#fff" weight="bold" size="md">SK</Txt>
                </View>
                <View>
                  <Txt weight="semibold" style={{ fontSize: 14 }}>{venueName}</Txt>
                  <Txt size="xs" color="ink3">Restaurant</Txt>
                </View>
              </Row>

              <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: 26, paddingBottom: 26, gap: 14 }}>
                {guestStep === 'qr' && (
                  <Col gap={16} style={{ alignItems: 'center' }}>
                    <View style={{ width: 170, height: 170, borderWidth: 1, borderColor: t.colors.line, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: t.colors.paper2 }}>
                      <Icon name="qr" size={120} color={t.colors.ink} strokeWidth={1.4} />
                    </View>
                    <View style={{ alignItems: 'center' }}>
                      <Txt weight="semibold" center style={{ fontSize: 18, letterSpacing: -0.18 }}>Join the waitlist</Txt>
                      <Txt size="md" color="ink3" center style={{ marginTop: 4 }}>Scan the table tent — or tap below to try it</Txt>
                    </View>
                    <Button variant="accent" size="lg" block onPress={() => s.guestScan()}>Tap to join</Button>
                  </Col>
                )}

                {guestStep === 'phone' && (
                  <Col gap={14}>
                    <View style={{ width: 54, height: 54, borderRadius: 14, backgroundColor: t.colors.accentSoft, alignItems: 'center', justifyContent: 'center', alignSelf: 'center' }}>
                      <Icon name="phone" size={26} color={t.colors.accent} strokeWidth={1.7} />
                    </View>
                    <View>
                      <Txt weight="semibold" center style={{ fontSize: 18 }}>Your number</Txt>
                      <Txt size="md" color="ink3" center style={{ marginTop: 3 }}>We'll text you the moment your table's ready</Txt>
                    </View>
                    <Input
                      size="lg"
                      style={{ textAlign: 'center', fontFamily: t.font.mono, fontSize: 16 } as unknown as StyleProp<ViewStyle>}
                      value={guestPhone}
                      onChangeText={s.setGuestPhone}
                    />
                    <Row gap={12} style={{ alignItems: 'center', justifyContent: 'center' }}>
                      <Txt size="md" color="ink3">Party size</Txt>
                      <Button variant="default" size="xs" icon onPress={() => s.setGuestPartySize(guestPartySize - 1)}>
                        <Icon name="minus" size={12} strokeWidth={2.2} color={t.colors.ink} />
                      </Button>
                      <Txt mono weight="semibold" style={{ minWidth: 20, textAlign: 'center' }}>{guestPartySize}</Txt>
                      <Button variant="default" size="xs" icon onPress={() => s.setGuestPartySize(guestPartySize + 1)}>
                        <Icon name="plus" size={12} strokeWidth={2.2} color={t.colors.ink} />
                      </Button>
                    </Row>
                    <Button variant="accent" size="lg" block onPress={handleGetCode}>Get code</Button>
                  </Col>
                )}

                {guestStep === 'otp' && (
                  <Col gap={16}>
                    <View>
                      <Txt weight="semibold" center style={{ fontSize: 18 }}>Enter the code</Txt>
                      <Txt size="md" color="ink3" center style={{ marginTop: 3 }}>Sent to {guestPhone}</Txt>
                    </View>
                    <Row gap={10} style={{ justifyContent: 'center' }}>
                      {guestOtp.map((d, i) => (
                        <TextInput
                          key={i}
                          ref={(r) => { otpRefs.current[i] = r; }}
                          value={d}
                          onChangeText={(v) => handleOtpChange(i, v)}
                          onKeyPress={(e) => handleOtpKeyPress(i, e.nativeEvent.key)}
                          keyboardType="number-pad"
                          maxLength={1}
                          textAlign="center"
                          style={{
                            width: 50, height: 60, borderWidth: 1.5, borderColor: t.colors.accentLine, borderRadius: 12,
                            backgroundColor: t.colors.accentSoft, color: t.colors.accent, fontFamily: t.font.mono,
                            fontWeight: '600', fontSize: 26,
                          }}
                        />
                      ))}
                    </Row>
                    <Row gap={8} style={{ justifyContent: 'center' }}>
                      <Txt size="sm" color="ink3">Enter the 4-digit code from the SMS</Txt>
                    </Row>
                    <Button variant="accent" size="lg" block onPress={handleJoinQueue}>Join queue</Button>
                  </Col>
                )}

                {gQueue && (
                  <Col gap={14} style={{ alignItems: 'center' }}>
                    <Txt style={{ fontSize: 14 }} color="ink3">You're in the queue</Txt>
                    <Txt mono weight="bold" color="accent" style={{ fontSize: 84, lineHeight: 76 }}>{queuePos}</Txt>
                    <Txt size="md" color="ink3">in line</Txt>
                    <Row gap={8} style={{ justifyContent: 'center' }}>
                      <Chip variant="neutral">{`${Math.max(0, queuePos - 1)} ahead`}</Chip>
                      <Chip variant="accent" dot>{`~${queuePos * 4} min`}</Chip>
                    </Row>
                    <Row gap={8} style={{ paddingVertical: 10, paddingHorizontal: 12, backgroundColor: t.colors.successSoft, borderRadius: 10, marginTop: 6 }}>
                      <View style={{ width: 7, height: 7, borderRadius: 3.5, backgroundColor: t.colors.success }} />
                      <Txt size="sm" color="success">SUKI is holding your spot · updates live</Txt>
                    </Row>
                  </Col>
                )}

                {gReady && (
                  <Col gap={16} style={{ alignItems: 'center' }}>
                    <View style={{ width: 78, height: 78, borderRadius: 39, backgroundColor: t.colors.successSoft, alignItems: 'center', justifyContent: 'center' }}>
                      <Check size={42} color={t.colors.success} sw={2} />
                    </View>
                    <View style={{ alignItems: 'center' }}>
                      <Txt weight="bold" center style={{ fontSize: 22, letterSpacing: -0.22 }}>Your table's ready!</Txt>
                      <Txt style={{ fontSize: 14, marginTop: 5 }} color="ink3" center>Please head to the host stand</Txt>
                    </View>
                    <View style={{ backgroundColor: t.colors.paper2, borderRadius: 10, paddingVertical: 12, paddingHorizontal: 14, alignSelf: 'stretch' }}>
                      <Txt size="md" color="ink2" center>Show this screen to the host at the door.</Txt>
                    </View>
                    <Button block onPress={() => s.guestReset()}>Start over</Button>
                  </Col>
                )}
              </View>
            </View>
          )}

          {/* ── WHATSAPP ── */}
          {guestView === 'whatsapp' && (
            <View style={{ flex: 1 }}>
              <Row gap={10} style={{ backgroundColor: '#075E54', paddingVertical: 10, paddingHorizontal: 16 }}>
                <View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: '#128C7E', alignItems: 'center', justifyContent: 'center' }}>
                  <Txt color="#fff" weight="bold" size="md">{venueInitials}</Txt>
                </View>
                <View>
                  <Txt color="#fff" weight="semibold" style={{ fontSize: 14 }}>{venueName}</Txt>
                  <Txt color="#fff" size="xs" style={{ opacity: 0.85 }}>Business · online</Txt>
                </View>
              </Row>

              <View style={{ flex: 1, backgroundColor: t.colors.paper2, paddingVertical: 16, paddingHorizontal: 14, gap: 12 }}>
                <View style={{ alignSelf: 'center', backgroundColor: t.colors.paper3, paddingVertical: 3, paddingHorizontal: 10, borderRadius: 8 }}>
                  <Txt size="xs" color="ink3">Today</Txt>
                </View>

                <View style={{ backgroundColor: t.colors.card, borderWidth: 1, borderColor: t.colors.line, borderRadius: 12, borderTopLeftRadius: 3, padding: 14, maxWidth: '92%', ...t.shadows.sm }}>
                  <Row gap={7} style={{ marginBottom: 10 }}>
                    <Check size={16} color={t.colors.success} sw={2} />
                    <Txt weight="semibold" color="success" size="md">{confirmedRes ? 'Booking confirmed' : 'No confirmed booking yet'}</Txt>
                  </Row>
                  <Col gap={9}>
                    <Spread><Txt size="md" color="ink3">Guest</Txt><Txt size="md" weight="medium">{confirmedRes?.name ?? '—'}</Txt></Spread>
                    <Spread><Txt size="md" color="ink3">Party</Txt><Txt size="md" weight="medium">{confirmedRes ? confirmedRes.size : '—'}</Txt></Spread>
                    <Spread><Txt size="md" color="ink3">When</Txt><Txt size="md" weight="medium">{confirmedRes ? displayTime(confirmedRes.time) : '—'}</Txt></Spread>
                    <Spread><Txt size="md" color="ink3">Table</Txt><Txt size="md" weight="medium">{confirmedRes?.table ?? confirmedRes?.section ?? '—'}</Txt></Spread>
                    <Divider style={{ marginVertical: 3 }} />
                    <Spread><Txt size="md" color="ink3">Ref</Txt><Txt size="md" mono weight="semibold" color="accent">{confirmedRes?.id ?? '—'}</Txt></Spread>
                  </Col>
                </View>

                <Txt style={{ alignSelf: 'flex-start', fontSize: 10, paddingLeft: 6 }} color="ink4">via SUKI · {venueName}</Txt>

                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 'auto' }}>
                  {WA_ACTIONS.map((l) => (
                    <Button key={l} size="sm" style={{ width: '48%', backgroundColor: t.colors.card }} onPress={() => toast(`${l} — opening…`, 'info')}>{l}</Button>
                  ))}
                </View>
              </View>
            </View>
          )}
        </View>
      </View>
    </Screen>
  );
}
